import React, { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, Pencil, Plus, Trash2, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { PlanEditor } from "./PlanEditor";
import { WholeClassPlanner } from "./WholeClassPlanner";
import { HomeInbox } from "./HomeInbox";
import { ResultsEditor } from "./ResultsEditor";

const PlanCard = ({ p, students, onEdit, onDelete }) => {
  const names = students.filter((s) => p.student_ids.includes(s.id)).map((s) => s.name);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`plan-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap gap-1">{[...(p.activities || []), ...p.chips].map((c) => <span key={c} className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">{c}</span>)}
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{p.period === "week" ? "This week" : "Today"}</span></div>
          {p.topic && <p className="mt-1 text-sm text-slate-800"><b>Topic:</b> {p.topic}</p>}
          {p.homework && <p className="text-sm text-slate-800" data-testid={`plan-hw-${p.id}`}><b>Homework:</b> {(p.homework_items?.length ? p.homework_items : [p.homework]).join(" · ")} <span className="text-xs text-slate-500">(due {p.homework_due})</span></p>}
          {p.test && <p className="text-sm text-violet-800"><b>Test:</b> {p.test.name} {p.test.mode === "marks" ? `/ ${p.test.out_of}` : "(grade A–E)"}</p>}
          {p.text && <p className="mt-1 text-sm text-slate-700">{p.text}</p>}
          <p className="mt-1 text-xs text-slate-500">{names.length} student(s): {names.slice(0, 6).join(", ")}{names.length > 6 ? "…" : ""}</p>
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={onEdit} className="rounded p-1 text-slate-500 hover:text-slate-900" aria-label="Edit plan" data-testid={`plan-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={onDelete} className="rounded p-1 text-slate-500 hover:text-red-600" aria-label="Delete plan" data-testid={`plan-del-${p.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default function ClassDiary() {
  const [meta, setMeta] = useState(null);
  const [klass, setKlass] = useState("all");
  const [date, setDate] = useState("");
  const [period, setPeriod] = useState("day");
  const [day, setDay] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reminder, setReminder] = useState(null);
  useEffect(() => { api.get("/teacher/diary/reminder").then((r) => setReminder(r.data.show ? r.data.message : null)).catch(() => {}); }, [day]);

  const loadMeta = useCallback(() => api.get("/teacher/diary/meta").then((r) => { setMeta(r.data); setDate((d) => d || r.data.today); }), []);
  const loadDay = useCallback(() => { if (date) api.get("/teacher/diary/day", { params: { d: date, klass } }).then((r) => setDay(r.data)); }, [date, klass]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { setDay(null); loadDay(); }, [loadDay]);

  const students = (day?.students || []);
  const onSchoolSet = (id, school) => setDay((d) => ({ ...d, students: d.students.map((s) => (s.id === id ? { ...s, school } : s)) }));
  const copyPrevious = async () => {
    const { data } = await api.get("/teacher/diary/previous", { params: { d: date, period } });
    const ids = new Set(students.map((s) => s.id));
    const todo = data.plans.map((p) => ({ ...p, student_ids: p.student_ids.filter((i) => ids.has(i)) })).filter((p) => p.student_ids.length);
    if (!todo.length) { toast.info(`No earlier ${period === "week" ? "weekly" : "daily"} plan to copy`); return; }
    for (const p of todo) await api.post("/teacher/diary/plans", { period, date, student_ids: p.student_ids, chips: p.chips, text: p.text,
      activities: p.activities || [], topic: p.topic || "", homework: p.homework || "", homework_items: p.homework_items || [], test: p.test || null });
    toast.success(`Copied ${todo.length} plan(s) from ${data.date}`);
    loadDay();
  };
  const del = async (p) => { await api.delete(`/teacher/diary/plans/${p.id}`); loadDay(); };

  if (!meta) return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;
  return (
    <div className="max-w-4xl space-y-5" data-testid="class-diary">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><NotebookPen className="h-5 w-5 text-emerald-700" /> Class Diary</h2>
        <p className="text-sm text-slate-500">Plan the day or week for a group of students, then after class tap only the students who differ.</p>
      </div>
      {reminder && <p className="rounded-xl bg-amber-100 p-3 text-sm font-semibold text-amber-900" data-testid="diary-reminder">{reminder}</p>}
      <HomeInbox />
      <div className="flex flex-wrap items-center gap-2">
        <select value={klass} disabled={!!editing} title={editing ? "Save or cancel the plan first" : undefined} onChange={(e) => setKlass(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="diary-class">
          <option value="all">All my students</option>
          {meta.classes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" value={date} max={meta.today} disabled={!!editing} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-md border border-slate-300 px-2 text-sm" data-testid="diary-date" />
        <div className="flex rounded-full bg-slate-100 p-1">
          {["day", "week"].map((p) => <button key={p} type="button" onClick={() => setPeriod(p)} data-testid={`diary-period-${p}`}
            className={`rounded-full px-3 py-1 text-sm font-semibold ${period === p ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{p === "day" ? "Today" : "This week"}</button>)}
        </div>
      </div>
      {!day ? <Loader2 className="h-5 w-5 animate-spin text-slate-500" /> : (
        <>
          <section className="space-y-2" data-testid="diary-plans">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-bold text-slate-800">Plan</h3>
              {!editing && <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copyPrevious} data-testid="plan-copy-previous"><Copy className="mr-1 h-4 w-4" /> Same as last {period === "week" ? "week" : "time"}</Button>
                <Button size="sm" onClick={() => setEditing({ mode: "class" })} className="bg-emerald-700 hover:bg-emerald-800" data-testid="plan-new"><Plus className="mr-1 h-4 w-4" /> Plan whole class</Button>
              </div>}
            </div>
            {editing?.mode === "class" && <WholeClassPlanner date={date} period={period} students={students} meta={meta} planned={day.plans.flatMap((p) => p.student_ids)}
              onSaved={() => { setEditing(null); loadDay(); loadMeta(); }} onCancel={() => setEditing(null)} onChipsChanged={loadMeta} onSchoolSet={onSchoolSet} />}
            {editing?.id && <PlanEditor key={editing.id} date={date} period={editing.period || period} students={students} meta={meta} initial={editing}
              taken={new Set(day.plans.filter((p) => p.id !== editing.id).flatMap((p) => p.student_ids))}
              onSaved={() => { setEditing(null); loadDay(); loadMeta(); }} onCancel={() => setEditing(null)} onChipsChanged={loadMeta} onSchoolSet={onSchoolSet} />}
            {day.plans.length === 0 && !editing && <p className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500" data-testid="plans-empty">No plan yet for this day. Tap <b>Plan whole class</b>, pick a group, untick what isn't done, add the next group, then <b>Save all plans</b>.</p>}
            {day.plans.map((p) => <PlanCard key={p.id} p={p} students={students} onEdit={() => setEditing(p)} onDelete={() => del(p)} />)}
          </section>
          <section className="space-y-2" data-testid="diary-results">
            <h3 className="font-bold text-slate-800">How it went</h3>
            <ResultsEditor day={day} klass={klass} onSaved={loadDay} />
          </section>
        </>
      )}
    </div>
  );
}
